<template>
  <div class="filters-by-category">
    <template
      v-for="category in allCategories"
      :key="category.id"
    >
      <UiExpandToggling
        v-if="categoryIsAvailable(category.id)"
        :key="category.id"
        :ref="el => { if (el) refs[category.id] = el}"
        :title="category.displayName + ' # ' + category.id"
        :expanded="expandedCategories.includes(category.id)"
        @expand="onExpand(category, 0)"
        @collapse="onCollapse(0, category)"
      >
        <template
          v-if="showAllCategories"
          #title="{ isExpand }"
        >
          <div
            class="filters-by-category__category"
            :class="{ isExpand }"
            @click="handleSubsubcategory(category)"
          >
            {{ category.displayName }}
          </div>
        </template>

        <template
          v-else-if="whereAreWe === 'selection'"
          #title="{ isExpand }"
        >
          <div
            class="filters-by-category__root"
            :class="{ isExpand }"
          >
            {{ category.displayName }}
          </div>
        </template>

        <template
          v-for="subcategory in category.children"
          :key="subcategory.id"
        >
          <UiExpandToggling
            v-if="categoryIsAvailable(subcategory.id)"
            :ref="el => { if (el) refs[subcategory.id] = el}"
            class="mb-5"
            :expanded="expandedCategories.includes(subcategory.id)"
            @expand="onExpand(subcategory, 1)"
            @collapse="onCollapse(1, subcategory)"
          >
            <template #title="{ isExpand }">
              <div
                class="ui-arrow-bracket"
                :class="{ isExpand }"
                @click="handleSubsubcategory(subcategory)"
              >
                {{ subcategory.displayName }}
              </div>
            </template>

            <template
              v-for="subsubcategory in subcategory.children"
              :key="subsubcategory.id"
            >
              <UiExpandToggling
                v-if="categoryIsAvailable(subsubcategory.id)"
                :ref="el => { if (el) refs[subsubcategory.id] = el}"
                class="mb-1"
                :expanded="expandedCategories.includes(subsubcategory.id)"
                @expand="onExpand(subsubcategory, 2)"
                @collapse="onCollapse(2, subsubcategory)"
              >
                <template #title>
                  <div
                    class="filters-by-category__subcategory"
                    :class="{'isSelected': expandedCategories.includes(subsubcategory.id)}"
                    @click="handleSubsubcategory(subsubcategory)"
                  >
                    {{ subsubcategory.displayName }}
                  </div>
                </template>

                <template v-if="subsubcategory.hasChildren && hasMore2AvailableCategories(subsubcategory.children)">
                  <div
                    class="filters-by-category__subcategory__all"
                    @click="showAllCategories ? toggleAllCategory(subsubcategory) : routerPush(subsubcategory.id)"
                  >
                    <UICheckbox
                      class="mb-3"
                      :value="showAllCategories ? categoryIsSelected(subsubcategory.id) : parentCategoryIsSelected(subsubcategory.id)"
                    >
                      {{ $t('common.all') }} {{ subsubcategory.displayName.toLowerCase() }}
                    </UICheckbox>
                  </div>

                  <template
                    v-for="subsubsubcategory in subsubcategory.children"
                    :key="subsubsubcategory.id"
                  >
                    <div
                      class="filters-by-category__checkbox"
                      :class="{
                        disabled: !categoryIsAvailable(subsubsubcategory.id),
                        hidden: showAllCategories && !categoryIsAvailable(subsubsubcategory.id),
                      }"
                      @click="toggleFewCategories(subsubsubcategory)"
                    >
                      <UICheckbox
                        class="mb-3"
                        :value="categoryIsSelected(subsubsubcategory.id)"
                      >
                        {{ subsubsubcategory.displayName }}
                      </UICheckbox>
                    </div>
                  </template>
                </template>
              </UiExpandToggling>
            </template>
          </UiExpandToggling>
        </template>
      </UiExpandToggling>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useCategoriesFilter } from '~/services/catalogFilter/categories';
import UICheckbox from '~/components/ui/UICheckbox.vue';
import { CategoryDTO } from '~/restAPI/data-contracts';
import { useCategoryTree } from '~/store/categoryTree';
import { isNumber } from '~/utils/extra';

const props = defineProps<{
  currentCategoryId?: number
  whereAreWe: 'catalog' | 'brand' | 'selection'
  showAllCategories?: boolean
}>();

const route = useRoute();
const router = useRouter();

const { allCategories, getCategoryById } = useCategoryTree();

const currentCategoryId = computed(() => {
  if (route.query?.categoriesIds) {
    return getCategoryById(Number(`${route.query.categoriesIds}`.split(',')[0]))?.id;
  }

  return props.currentCategoryId;
});

const currentCategoryIds = computed(() => {
  if (!props.showAllCategories) return [];

  if (route.query?.categoriesIds) {
    return `${route.query.categoriesIds}`.split(',')
      .map((id) => getCategoryById(Number(id))?.id)
      .filter((id) => isNumber(id));
  }

  return [];
});

const weInsideBrands = computed(() => !!route.params?.brand);
const rootCategory = computed(() => getCategoryById(getCategoryById(currentCategoryId.value)?.rootCategoryId));

const rootCategories = computed(() => {
  if (!props.showAllCategories) return [];

  const result = currentCategoryIds.value
    .map((id) => getCategoryById(getCategoryById(id)?.rootCategoryId));

  return result;
});

function routerPush(categoriesIds: string | number | undefined) {
  if (weInsideBrands.value || props.whereAreWe === 'selection') {
    router.push({
      query: {
        ...route.query,
        categoriesIds,
      },
    });
  } else {
    const category = getCategoryById(+categoriesIds!) ?? rootCategory.value;

    router.push(`/catalog/${category.url}`);
  }
}

const {
  refs,
  availableCategories,
  selectedCategories,
  categoryIsAvailable,
  hasMore2AvailableCategories,
  categoryIsSelected,
  expandedCategories,
  onExpand,
  onCollapse,
  availableCategoriesParents,
} = useCategoriesFilter(route, (props.showAllCategories ? undefined : routerPush));

function parentCategoryIsSelected(id: number) {
  if ([
    'selection',
    'brand',
  ].includes(props.whereAreWe)) {
    return expandedCategories.value.includes(id) && selectedCategories.value.has(getCategoryById(id).id);
  }

  return (id === props.currentCategoryId) && !selectedCategories.value.size;
}

function toggleFewCategories(category: CategoryDTO, isAddCategory?: boolean) {
  if (props.showAllCategories) {
    if (category.hasChildren
      && selectedCategories.value.size === 1
      && !route.query.categoriesIds?.includes(String(category.id))
    ) {
      selectedCategories.value.clear();
    }

    if (!category.hasChildren) {
      const cat = getCategoryById(category.id!);

      selectedCategories.value.delete(cat.parentId!);
    }
  } else if (selectedCategories.value.size === 1 && expandedCategories.value.includes(+route.query.categoriesIds!)) {
    selectedCategories.value.clear();
  }

  if (selectedCategories.value.has(category.id!)) {
    selectedCategories.value.delete(category.id!);
  } else if (!props.showAllCategories
    || (props.showAllCategories
    && !category.children?.find((c) => c.id === +route.query.categoriesIds!)
    && !(category.hasChildren && expandedCategories.value.includes(category.id)))
  ) {
    selectedCategories.value.add(category.id!);
  } else if (isAddCategory) {
    selectedCategories.value.add(category.id!);
  }

  const query = { ...route.query };

  if (selectedCategories.value.size > 0) {
    query.categoriesIds = `${[...selectedCategories.value]}`;
  } else if (props.whereAreWe === 'selection') {
    query.categoriesIds = getCategoryById(category.id!).parentId;
  } else {
    delete query.categoriesIds;
  }

  router.push({ query });
}

function fillExpandedCategories(id: number) {
  const category = getCategoryById(id);

  if (!category) return;

  if (props.showAllCategories) {
    if (!rootCategories.value.some((c) => c.id === category.parentId)) {
      fillExpandedCategories(category.parentId);
    }
  } else if (category.parentId !== rootCategory.value.id) {
    fillExpandedCategories(category.parentId);
  }

  if ([
    'selection',
    'brand',
  ].includes(props.whereAreWe) && !route.query?.categoriesIds) return;

  expandedCategories.value.push(category.id);
}

function toggleAllCategory(subcategory: CategoryDTO) {
  subcategory.children?.forEach((category) => {
    if (categoryIsAvailable(category.id!)) {
      selectedCategories.value.delete(category.id!);
    }
  });

  toggleFewCategories(subcategory, true);
}

function handleSubsubcategory(category: CategoryDTO) {
  if (!props.showAllCategories) return;

  selectedCategories.value.clear();

  toggleFewCategories(category);
}

watch(() => availableCategories.value?.length && allCategories.value.length, (isReady) => {
  if (!isReady) return;

  if (!currentCategoryId.value) return;

  if (props.showAllCategories) {
    expandedCategories.value = [];

    currentCategoryIds.value.forEach((id) => {
      const rootId = getCategoryById(id!)?.rootCategoryId;

      if (rootId && !expandedCategories.value.includes(rootId)) {
        expandedCategories.value.push(rootId);
      }

      fillExpandedCategories(id!);
    });
  } else {
    expandedCategories.value = [rootCategory.value.id];
    fillExpandedCategories(currentCategoryId.value);
  }
}, { immediate: true });
</script>

<style scoped lang="scss" src="~/components/filters/categories-tree.scss">
</style>
